import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Stack, IconButton, InputAdornment, TextField } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import * as yup from 'yup';
import PasswordChecklist from 'react-password-checklist'

import { resetPassword } from 'services/requests/auth';
import { toast } from 'utils';

import Iconify from 'components/Iconify';

const initialValues = {
  password: '',
  cpassword: ''
};

export default function ResetForm() {
  const { t } = useTranslation('common');
  const { token } = useParams();
  const navigate = useNavigate();

  const [showPassword, setShowPassword] = useState(false);
  const [showCPassword, setShowCPassword] = useState(false);

  const validationSchema = yup.object({
    password: yup.string()
      .password('A senha deve conter pelo menos 8 caracteres, contendo pelo menos 1 número, 1 caracter especial e uma letra maiúscula')
      .required('Obrigatório'),
    cpassword: yup.string()
      .required('Obrigatório')
      .oneOf([yup.ref('password'), null], 'Senha e confirmação não conferem'),
  });

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: (values, { setSubmitting }) => {
      resetPassword({ body: { password: values.password, token } })
        .then(() => {
          toast(t('auth.reset.success'), 'success')
          navigate('/auth', { replace: true })
        })
        .catch((err) => toast(err, 'error'))
        .then(() => setSubmitting(false));
    },
  });

  return (
    <>
      <Stack spacing={3}>
        <TextField
          name="password"
          label={t('auth.reset.form.password')}
          type={showPassword ? 'text' : 'password'}
          value={formik.values.password}
          onChange={formik.handleChange}
          error={formik.touched.password && Boolean(formik.errors.password)}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                  <Iconify icon={showPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <TextField
          name="cpassword"
          label={t('auth.reset.form.cpassword')}
          type={showCPassword ? 'text' : 'password'}
          value={formik.values.cpassword}
          onChange={formik.handleChange}
          error={formik.touched.cpassword && Boolean(formik.errors.cpassword)}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowCPassword(!showCPassword)} edge="end">
                  <Iconify icon={showCPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <div className="password-checklist">
          <PasswordChecklist
            rules={['minLength', 'specialChar', 'number', 'capital', 'match']}
            minLength={8}
            value={formik.values.password}
            valueAgain={formik.values.cpassword}
            messages={{
              minLength: 'A senha deve conter pelo menos 8 caracteres.',
              specialChar: 'Pelo menos um caracter especial.',
              number: 'Pelo menos um número.',
              capital: 'Pelo menos uma letra maiúscula',
              match: 'Senha e confirmação não conferem'
            }}
          />
        </div>
      </Stack>

      <LoadingButton
        fullWidth
        size="large"
        type="submit"
        color="secondary"
        variant="contained"
        onClick={() => formik.handleSubmit()}
        loading={formik.isSubmitting}
        sx={{ mt: 3 }}
      >
        {t('auth.reset.form.submit')}
      </LoadingButton>
    </>
  );
}
